import React, { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { featureFlagsApi } from '../api/featureFlagsApi';
import { FeatureFlag } from '../models/FeatureFlag';
import { FLAG_DEFAULTS } from '../constants/featureFlagNames';
import { queryKeys } from '../../../api/queryKeys';

const FeatureFlagContext = createContext({
    flags: [],
    flagsMap: new Map(),
    loading: true,
    error: null,
    isEnabled: () => false,
    refresh: () => {},
});

/**
 * Centralized runtime feature flag provider.
 * - Fetches public flags once via React Query (cached)
 * - Provides safe defaults when API fails (does not block app)
 * - Respects runtime changes without rebuild (refetchOnWindowFocus, staleTime 60s)
 * - Does not request on every render (single query, shared cache)
 */
export const FeatureFlagProvider = ({ children }) => {
    const query = useQuery({
        queryKey: queryKeys.featureFlagsPublic,
        queryFn: () => featureFlagsApi.getPublicFlags(),
        select: (data) => (Array.isArray(data) ? data : []).map((f) => new FeatureFlag(f)),
        staleTime: 60 * 1000,
        gcTime: 5 * 60 * 1000,
        refetchOnWindowFocus: true,
        refetchInterval: 60 * 1000,
        retry: 1,
    });

    const flags = query.data || [];
    const flagsMap = useMemo(() => {
        const map = new Map();
        flags.forEach((f) => map.set(f.flagName, f));
        return map;
    }, [flags]);

    const isEnabled = useMemo(() => {
        return (flagName) => {
            const flag = flagsMap.get(flagName);
            if (flag) return flag.enabled();
            // Safe fallback: use backend default when flag not yet loaded or API failed
            if (FLAG_DEFAULTS.hasOwnProperty(flagName)) {
                return FLAG_DEFAULTS[flagName] === true;
            }
            // Unknown flag → disabled (safe default)
            return false;
        };
    }, [flagsMap]);

    const value = useMemo(
        () => ({
            flags,
            flagsMap,
            loading: query.isLoading,
            error: query.error || null,
            isEnabled,
            refresh: query.refetch,
        }),
        [flags, flagsMap, query.isLoading, query.error, isEnabled, query.refetch],
    );

    return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
};

export const useFeatureFlagContext = () => {
    const ctx = useContext(FeatureFlagContext);
    if (!ctx) {
        throw new Error('useFeatureFlagContext must be used within FeatureFlagProvider');
    }
    return ctx;
};

export default FeatureFlagProvider;
